import { useQueryClient } from "@tanstack/react-query";
import useProfile from "../../hooks/data/useProfile";
import { useState, createContext, useContext, useMemo, useRef, useEffect } from "react";
import { useFirebase } from "../firebase";
import {
  arrayRemove,
  arrayUnion,
  documentId,
  increment,
  orderBy,
  QueryDocumentSnapshot,
  serverTimestamp,
  where,
} from "firebase/firestore";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import { useParams } from "react-router";

/**
 * Context for sharing GrooveRoom functionality across components
 * @typedef {Object} GrooveRoomContextType
 * @property {(params: {userId: string}) => Promise<Array>} listRoomsByUser - Function to list rooms by user ID
 * @property {(params: {roomId: string}) => Promise<Object>} getRoom - Function to get a specific room by ID
 * @property {() => Promise<Object>} addNewRoom - Function to create a new room
 * @property {(params: {roomId: string, postId: string, userId: string}) => Promise<void>} addTrackToRoom - Function to add a track to a room
 * @property {(params: {roomId: string, userId: string}) => Promise<void>} addMemberToRoom - Function to add a member to a room
 * @property {(params: {roomId: string, userId: string}) => Promise<void>} addJoinRequestToRoom - Function to add a join request
 * @property {(params: {roomId: string, userId: string}) => Promise<void>} removeJoinRequestFromRoom - Function to remove a join request
 * @property {(params: {roomId: string, data: Object}) => Object} updateRoom - Function to update room data
 * @property {(params: {roomId: string, item: {userId: string, postId: string}}) => void} removeTrackFromRoom - Function to remove a track from a room
 * @property {(params: {roomId: string, softDelete?: boolean}) => Promise<void>} deleteRoom - Function to delete a room
 * @property {(params: {roomId: string}) => Promise<void>} joinRoomWS - Function to join a room via WebSocket
 * @property {(params: {roomId: string, type: 'chat' | 'audio-update' | 'audio-suggestion', text: string, customPayload: object}) => Promise<void>} sendMessageToRoom - Function to send a message to a room
 * @property {(params: {roomId: string, userId: string}) => Promise<void>} removeMemberFromRoom - Function to remove a member from a room
 * @property {(params: {roomId: string, userId: string}) => Promise<void>} leaveRoom - Function to leave the room
 * @property {(params: {roomId: string, data: Object}) => void} updateRoomStateOnly - Function to update room state only
 * @property {(params: {roomId: string, data: Object}) => void} addInvitee - Add yourself as an invitee to the room
 * @property {boolean} isWsJoined - Whether WebSocket is currently joined
 * @property {Function} closeRoomWS - Closes the room websocket connection
 * @property {Array} messages - Array of messages from the room
 */
const GrooveRoomContext = createContext(null);

/**
 * Provider component that provides GrooveRoom functionality to its children
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Provider component
 */
export const GrooveRoomProvider = ({ children }) => {
  const { getData, addDocument, updateDocument } = useFirebase();
  const queryClient = useQueryClient();

  const wsRef = useRef(null);
  const [isWsJoined, setIsWsJoined] = useState(false);
  const { currentUser } = useFirebase();

  const user = useUserStore((state) => state.user, shallow) || {};

  const { id: stateUserId, username: stateUsername } = user;
  const [messages, setMessages] = useState([]);

  async function getRoom({ roomId }) {
    let Room = queryClient.getQueryData(["rooms", roomId]);

    if (!Room) {
      console.log("Room is not in cache");
      Room = await getData({
        path: `rooms/${roomId}`,
      });
      if (!Room) throw new Error("Room does not exist");
      queryClient.setQueryData(["rooms", roomId], () => Room);
    }

    return Room;
  }

  async function listRoomsByUser({ userId }) {
    try {
      // Query with only the members filter - isDeleted is handled in security rules
      const { docs: Rooms, last: lastRoomRef } = await getData({
        path: `rooms`,
        limit: 10,
        filters: [
          where("members", "array-contains", userId),
          orderBy("createdAt", "desc"),
        ],
      });

      // Cache individual rooms
      Rooms?.forEach((Room) => {
        queryClient.setQueryData(["rooms", Room.id], (oldData = {}) => ({
          ...oldData,
          ...Room,
        }));
      });

      // Cache the last room reference for pagination
      queryClient.setQueryData(["rooms", "lastRoomRef"], () => lastRoomRef);

      return Rooms || [];
    } catch (error) {
      console.error("Error fetching rooms:", error);
      return [];
    }
  }

  async function addNewRoom() {
    const data = {
      name: "New Room",
      createdAt: serverTimestamp(),
      isDeleted: false,
      isPublic: true,
      owner: stateUserId,
      members: [stateUserId],
      admins: [stateUserId],
    };
    const Room = await addDocument({
      path: `rooms`,
      data,
    });
    queryClient.setQueryData(["rooms", Room.id], () => Room);

    return Room;
  }

  function updateRoom({ roomId, data }) {
    queryClient.setQueryData(["rooms", roomId], (oldData) => ({
      ...oldData,
      ...data,
    }));

    (async () => {
      try {
        await updateDocument({
          path: `rooms/${roomId}`,
          data,
        });
      } catch (err) {
        console.error("Failed to update Room on db:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();

    return data;
  }

  async function updateRoomAsync({ roomId, data }) {
    try {
      await updateDocument({
        path: `rooms/${roomId}`,
        data,
      });
      queryClient.setQueryData(["rooms", roomId], (oldData) => ({
        ...oldData,
        ...data,
      }));
    } catch (err) {
      console.error("Failed to update Room on db:", err);
      // optionally: rollback queryClient.setQueryData here
    }

    return data;
  }

  async function deleteRoom({ roomId, softDelete }) {
    try {
      if (!softDelete) {
        await updateDocument({
          path: `rooms/${roomId}`,
          data: {
            isDeleted: true,
            deletedAt: serverTimestamp(),
          },
        });
      }
      queryClient.removeQueries({
        queryKey: ["rooms", roomId],
      });
    } catch (err) {
      console.error("Failed to update Room on db:", err);
      // optionally: rollback queryClient.setQueryData here
    }
  }

  function leaveRoom({ roomId, userId }) {
    deleteRoom({ roomId, softDelete: true });
    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          members: arrayRemove(userId),
        },
      }))();
  }

  async function addTrackToRoom({ roomId, postId, userId }) {
    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          tracks: arrayUnion({
            userId,
            postId,
          }),
        },
      }))();

    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const tracks = [...(prev?.tracks || [])];
      // Check if item already exists
      const exists = tracks.some(
        (track) => tracks.userId === userId && track.postId === postId
      );
      if (!exists) {
        tracks.push({ userId, postId });
      }
      return {
        ...prev,
        tracks,
      };
    });
  }

  async function addMemberToRoom({ roomId, userId }) {
    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const members = [...(prev?.members || [])];
      if (!members?.includes(userId)) members.push(userId);
      const joinRequests = [...(prev?.joinRequests || [])]?.filter(
        (uid) => uid !== userId
      );

      return { ...prev, members, joinRequests };
    });
    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          members: arrayUnion(userId),
          joinRequests: arrayRemove(userId),
        },
      }))();
  }

  async function addJoinRequestToRoom({ roomId, userId }) {
    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const joinRequests = [...(prev?.joinRequests || [])];
      if (!joinRequests?.includes(userId)) joinRequests.push(userId);

      return { ...prev, joinRequests };
    });
    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          joinRequests: arrayUnion(userId),
        },
      }))();
  }

  async function removeJoinRequestFromRoom({ roomId, userId }) {
    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const joinRequests = [...(prev?.joinRequests || [])]?.filter(
        (uid) => uid !== userId
      );
      return { ...prev, joinRequests };
    });

    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          joinRequests: arrayRemove(userId),
        },
      }))();
  }

  async function removeMemberFromRoom({ roomId, userId }) {
    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const members = [...(prev?.members || [])];
      const updatedMembers = members.filter((id) => id !== userId);
      return { ...prev, members: updatedMembers };
    });
    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          members: arrayRemove(userId),
        },
      }))();
  }

  function removeTrackFromRoom({ roomId, item }) {
    let updatedTracks = [];

    queryClient.setQueryData(["rooms", roomId], (prev) => {
      const tracks = [...(prev.tracks || [])];
      const filteredTracks = tracks.filter(
        (_track) =>
          !(_track.userId === item.userId && _track.postId === item.postId)
      );
      updatedTracks = filteredTracks;
      return {
        ...prev,
        tracks: filteredTracks,
      };
    });

    (async () =>
      await updateDocument({
        path: `rooms/${roomId}`,
        data: {
          tracks: arrayRemove(item),
        },
      }))();
  }

  async function joinRoomWS({ roomId }) {
    if (isWsJoined || !stateUsername || !stateUserId) return;

    const room = await getRoom({ roomId });
    if (!room?.isOpenNow) {
      if (room?.owner === stateUserId || room?.admins?.includes(stateUserId)) {
        await updateRoomAsync({
          roomId,
          data: {
            isOpenNow: true,
            lastStartedAt: serverTimestamp(),
          },
        });
      }
    }

    const wsURL = "ws://localhost:8080";
    const idToken = await currentUser.getIdToken();

    return new Promise((resolve, reject) => {
      const socket = new WebSocket(
        `${wsURL}?username=${stateUsername}&token=${idToken}&roomId=${roomId}`
      );

      // Store the socket in ref for immediate access
      wsRef.current = socket;

      socket.onopen = (event) => {
        console.log("Room has been joined...");
        setIsWsJoined(true);
        // Add a small delay to ensure socket is fully ready
        setTimeout(() => {
          resolve(socket); // Resolve when socket is successfully opened
        }, 50);
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        wsRef.current = null; // Clear the socket on error
        reject(new Error("Failed to connect to room"));
      };

      socket.onclose = (event) => {
        console.log("Socket was closed:", event.reason);
        setIsWsJoined(false);
        wsRef.current = null; // Clear the socket when closed
        if (event.code === 1008) {
          reject(new Error(event.reason));
        }
      };

      socket.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        setMessages((prev) => [...prev, msg]);
      };
    });
  }

  function closeRoomWS() {
    wsRef.current?.close();
    setMessages([]);
  }

  /**
   * Sends a message to the room via WebSocket.
   *
   * @param {Object} params - The message parameters.
   * @param {'chat' | 'audio-update' | 'audio-suggestion'} params.type - The type of message to send.
   * @param {string} params.text - The message text.
   * @returns {Promise<void>}
   */
  async function sendMessageToRoom({ roomId, type, text, customPayload }) {
    const currentRoom = await getRoom({ roomId });
    const allowMessage =
      type === "chat" ||
      type === "audio-suggestion" ||
      currentRoom.owner === stateUserId;

    if (allowMessage) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // console.log("Sending the message...");
        try {
          wsRef.current.send(JSON.stringify({ type, text, customPayload }));
          // console.log("Message sent successfully");
        } catch (error) {
          console.error("Error sending message:", error);
        }
      } else {
        console.warn("WebSocket not ready:", {
          hasSocket: !!wsRef.current,
          readyState: wsRef.current?.readyState,
          isWsJoined,
        });
      }
    }
  }

  function updateRoomStateOnly({ roomId, data }) {
    queryClient.setQueryData(["rooms", roomId], (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        ...data,
      };
    });
  }

  function addInvitee({ roomId, data }) {
    queryClient.setQueryData(
      ["room-invitees", roomId, stateUserId],
      () => data
    );
  }

  const value = useMemo(
    () => ({
      listRoomsByUser,
      getRoom,
      addNewRoom,
      addTrackToRoom,
      addMemberToRoom,
      updateRoom,
      removeTrackFromRoom,
      deleteRoom,
      joinRoomWS,
      closeRoomWS,
      sendMessageToRoom,
      removeMemberFromRoom,
      updateRoomStateOnly,
      addJoinRequestToRoom,
      removeJoinRequestFromRoom,
      leaveRoom,
      addInvitee,
      isWsJoined,
      messages,
    }),
    [isWsJoined, wsRef.current, messages, currentUser, stateUsername]
  );

  return (
    <GrooveRoomContext.Provider value={value}>
      {children}
    </GrooveRoomContext.Provider>
  );
};

/**
 * Hook to access GrooveRoom functionality from context
 * @returns {GrooveRoomContextType} GrooveRoom context value
 * @throws {Error} If used outside of GrooveRoomProvider
 */
export const useGrooveRoom = () => {
  const context = useContext(GrooveRoomContext);

  if (!context) {
    throw new Error("useGrooveRoom must be used within a GrooveRoomProvider");
  }

  return context;
};
